import { HOLD_LEVEL_LEGEND } from "../../utils/eventStatus";
import React, { useEffect, useState } from "react";

import { CurrencyInput } from "../CurrencyInput";
import { MobileSelect } from "../ui/MobileSelect";
import { formatVenueDisplayAddress, venuePrimaryCity } from "../../utils/venueAddress";
import { artistDisplayName } from "../../utils/artistProfile";
import {
  EVENT_STATUS_OPTIONS,
  parseVenueCapacity,
  ticketCapacityMessage,
  tierBelowSoldMessage,
  totalTierQuantity,
  type TicketTierDraft,
} from "../../utils/eventForm";
import { isPastDateKey, todayDateKey } from "../../utils/calendarDates";
import type { Artist, Venue } from "../../types";
import "./EventFormFields.css";

export type EventFormValues = {
  eventName: string;
  selectedArtistId: string;
  eventDate: string;
  startAt: string;
  endAt: string;
  venueId: string;
  eventCapacity: string;
  ticketTiers: TicketTierDraft[];
  productionCost: string;
  marketingCost: string;
  artistFee: string;
  status: string;
  /** "" = no hold, otherwise "1" | "2" | "3". */
  holdLevel: string;
};

type Props = {
  mode: "create" | "edit";
  artists: Artist[];
  venues: Venue[];
  values: EventFormValues;
  onPatch: (patch: Partial<EventFormValues>) => void;
  onPatchTier: (tierId: string, patch: Partial<TicketTierDraft>) => void;
  onAddTier: () => void;
  onRemoveTier: (tierId: string) => void;
  showStatus?: boolean;
  statusOptions?: readonly string[];
  originalEventDate?: string;
};

const EventFormFields: React.FC<Props> = ({
  mode,
  artists,
  venues,
  values,
  onPatch,
  onPatchTier,
  onAddTier,
  onRemoveTier,
  showStatus = mode === "edit",
  statusOptions = EVENT_STATUS_OPTIONS,
  originalEventDate,
}) => {
  const selectedVenue = venues.find((venue) => venue.id === values.venueId) ?? null;
  const eventCityFromVenue = selectedVenue ? venuePrimaryCity(selectedVenue) : "";
  const sectionPrefix = mode === "create" ? "onb" : "evt";

  // FE-5: block obvious year typos (the browser's date input otherwise
  // accepts year 0002 if the user mistypes). The min is "today" — events
  // can't be created in the past. The min for start/end is the chosen
  // event date so the auto-fill from EventOnboardingPage produces valid
  // values that survive backend validation.
  const [todayStr, setTodayStr] = useState(() => todayDateKey());
  useEffect(() => {
    const id = window.setInterval(() => setTodayStr(todayDateKey()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const dateMin =
    mode === "create"
      ? todayStr
      : originalEventDate && isPastDateKey(originalEventDate)
        ? undefined
        : todayStr;
  const dateTimeMinBase = values.eventDate || todayStr;
  const dateTimeMin =
    mode === "create" || (mode === "edit" && (!originalEventDate || !isPastDateKey(originalEventDate)))
      ? `${dateTimeMinBase}T00:00`
      : undefined;
  // Inline range check: end must be strictly after start.
  const endBeforeStart =
    !!values.startAt && !!values.endAt && new Date(values.endAt) <= new Date(values.startAt);

  const capacity = parseVenueCapacity(values.eventCapacity);
  const allocatedTickets = totalTierQuantity(values.ticketTiers);
  const overCapacity = capacity !== null && allocatedTickets > capacity;

  return (
    <>
      <div className="app-form-section" role="group" aria-labelledby={`${sectionPrefix}-sec-show`}>
        <div id={`${sectionPrefix}-sec-show`} className="app-form-section-title">
          Show details
        </div>
        <p className="app-form-section-lede">
          {mode === "create"
            ? "What you are promoting, when it happens, and where — the event city and address come from the venue you select."
            : "Update core event metadata. City is derived from the selected venue."}
        </p>
        <div className="eventos-form-grid">
          <label className="eventos-field">
            <span className="eventos-field-label">Event name</span>
            <input
              className="eventos-input"
              value={values.eventName}
              onChange={(ev) => onPatch({ eventName: ev.target.value })}
              placeholder="Event or show title"
              required
            />
          </label>
          <label className="eventos-field">
            <span className="eventos-field-label">Artist / headliner</span>
            <MobileSelect
              label="Artist"
              className="eventos-select"
              value={values.selectedArtistId}
              onChange={(v) => onPatch({ selectedArtistId: v })}
              options={[
                { value: "", label: "Select artist (optional)" },
                ...artists.map((artist) => ({ value: artist.id, label: artistDisplayName(artist) })),
              ]}
            />
            {artists.length === 0 ? (
              <span className="eventos-subtle">Add artists in the registry to link a headliner.</span>
            ) : null}
          </label>
          {showStatus ? (
            <label className="eventos-field">
              <span className="eventos-field-label">Lifecycle status</span>
              <MobileSelect
                label="Status"
                className="eventos-select"
                value={values.status}
                onChange={(v) => onPatch({ status: v })}
                options={Array.from(new Set([...statusOptions, values.status].filter(Boolean))).map((status) => ({
                  value: status,
                  label: status.replace(/_/g, " "),
                }))}
              />
            </label>
          ) : null}
          {showStatus ? (
            <label className="eventos-field">
              <span className="eventos-field-label">Hold level</span>
              <MobileSelect
                label="Hold level"
                className="eventos-select"
                value={values.holdLevel}
                onChange={(v) => onPatch({ holdLevel: v })}
                options={[
                  { value: "", label: "Not on hold" },
                  ...[1, 2, 3].map((level) => ({ value: String(level), label: HOLD_LEVEL_LEGEND[level] })),
                ]}
              />
            </label>
          ) : null}
          <label className="eventos-field event-form-fields__venue-field">
            <span className="eventos-field-label">Venue</span>
            <MobileSelect
              label="Venue"
              className="eventos-select"
              value={values.venueId}
              onChange={(nextId) => {
                const venue = venues.find((item) => item.id === nextId);
                onPatch({
                  venueId: nextId,
                  ...(venue?.capacity ? { eventCapacity: String(venue.capacity) } : {}),
                });
              }}
              options={[
                { value: "", label: "Select venue" },
                ...venues.map((venue) => ({
                  value: venue.id,
                  label: `${venue.venue_name} — ${formatVenueDisplayAddress(venue)}`,
                })),
              ]}
            />
            {venues.length === 0 ? (
              <span className="eventos-subtle">Add a venue in the registry before saving.</span>
            ) : null}
            {selectedVenue ? (
              <div className="event-form-fields__venue-location" aria-live="polite">
                <span className="event-form-fields__venue-location-label">Event location</span>
                <span>{formatVenueDisplayAddress(selectedVenue)}</span>
                {eventCityFromVenue ? (
                  <span className="event-form-fields__venue-location-city">City for listings: {eventCityFromVenue}</span>
                ) : (
                  <span className="event-form-fields__venue-location-warn">
                    This venue is missing a city — update it under Venues before saving.
                  </span>
                )}
              </div>
            ) : null}
          </label>
          <label className="eventos-field">
            <span className="eventos-field-label">Main event date</span>
            <input
              className="eventos-input"
              type="date"
              value={values.eventDate}
              min={dateMin}
              onChange={(ev) => onPatch({ eventDate: ev.target.value })}
              required
            />
          </label>
          <label className="eventos-field">
            <span className="eventos-field-label">Event start date and time</span>
            <input
              className="eventos-input"
              type="datetime-local"
              value={values.startAt}
              min={dateTimeMin}
              onChange={(ev) => onPatch({ startAt: ev.target.value })}
              required
            />
          </label>
          <label className="eventos-field">
            <span className="eventos-field-label">Event end date and time</span>
            <input
              className="eventos-input"
              type="datetime-local"
              value={values.endAt}
              min={values.startAt || dateTimeMin}
              onChange={(ev) => onPatch({ endAt: ev.target.value })}
              aria-invalid={endBeforeStart || undefined}
              required
            />
            {endBeforeStart ? (
              <span className="event-form-fields__venue-location-warn">
                End must be after the start date and time.
              </span>
            ) : null}
          </label>
          <label className="eventos-field">
            <span className="eventos-field-label">Venue capacity</span>
            <input
              className="eventos-input"
              type="number"
              min={1}
              value={values.eventCapacity}
              onChange={(ev) => onPatch({ eventCapacity: ev.target.value })}
              placeholder="Total capacity at venue"
              required
            />
          </label>
        </div>
      </div>

      <div className="app-form-section" role="group" aria-labelledby={`${sectionPrefix}-sec-tier`} id="event-form-tiers">
        <div id={`${sectionPrefix}-sec-tier`} className="app-form-section-title">
          Ticket tiers
        </div>
        <p className="app-form-section-lede">
          {mode === "edit"
            ? "Update tier definitions. Sold counts are managed on the dashboard inventory table."
            : "Add one or more tiers before submit. Amounts are in rupees."}
        </p>
        <div className="event-form-fields__tiers-list">
          {values.ticketTiers.map((tier, index) => (
            <div key={tier.id} className="eventos-form-grid event-form-fields__tier-row">
              <label className="eventos-field">
                <span className="eventos-field-label">Tier name</span>
                <input
                  className="eventos-input"
                  value={tier.tier_name}
                  onChange={(ev) => onPatchTier(tier.id, { tier_name: ev.target.value })}
                  // FE-3: select existing tier name on focus so the user can
                  // overwrite (instead of having to manually clear "General"
                  // or a previous draft).
                  onFocus={(ev) => ev.target.select()}
                  placeholder={index === 0 ? "General Admission" : "VIP / Lounge / Early Bird"}
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Ticket price</span>
                <CurrencyInput
                  value={tier.price}
                  onChange={(ev) => onPatchTier(tier.id, { price: ev.target.value })}
                  placeholder="Price per ticket"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Ticket quantity</span>
                <input
                  className="eventos-input"
                  type="number"
                  min={1}
                  value={tier.quantity}
                  onChange={(ev) => onPatchTier(tier.id, { quantity: ev.target.value })}
                  placeholder="Tickets available"
                  required
                />
                {mode === "edit" && tier.serverId && (tier.sold ?? 0) > 0 && Number(tier.quantity) < (tier.sold ?? 0) ? (
                  <span className="event-form-fields__venue-location-warn">
                    {tierBelowSoldMessage(tier.tier_name, tier.sold ?? 0)}
                  </span>
                ) : null}
              </label>
              {mode === "edit" && tier.serverId && tier.sold !== undefined ? (
                <div className="eventos-field event-form-fields__sold-readonly">
                  <span className="eventos-field-label">Sold (read-only)</span>
                  <span className="event-form-fields__sold-value">{tier.sold.toLocaleString("en-IN")}</span>
                </div>
              ) : null}
              <div className="event-form-fields__tier-actions">
                <button
                  type="button"
                  className="eventos-nav-btn"
                  disabled={values.ticketTiers.length === 1}
                  onClick={() => onRemoveTier(tier.id)}
                >
                  Remove tier
                </button>
              </div>
            </div>
          ))}
          <div className="event-form-fields__add-tier-wrap">
            <button type="button" className="event-form-fields__add-tier-btn" onClick={onAddTier}>
              <span className="event-form-fields__add-tier-icon" aria-hidden>
                +
              </span>
              <span className="event-form-fields__add-tier-text">Add ticket tier</span>
            </button>
          </div>
          {capacity !== null ? (
            overCapacity ? (
              <span className="event-form-fields__venue-location-warn" role="alert">
                {ticketCapacityMessage(allocatedTickets, capacity)}
              </span>
            ) : (
              <span className="eventos-subtle">
                {allocatedTickets.toLocaleString("en-IN")} of {capacity.toLocaleString("en-IN")} tickets allocated
              </span>
            )
          ) : null}
        </div>
      </div>

      <div className="app-form-section" role="group" aria-labelledby={`${sectionPrefix}-sec-costs`}>
        <div id={`${sectionPrefix}-sec-costs`} className="app-form-section-title">
          Cost assumptions
        </div>
        <p className="app-form-section-lede">Used for P&amp;L and scenario tooling. Amounts are in rupees.</p>
        <div className="eventos-form-grid">
          <label className="eventos-field">
            <span className="eventos-field-label">Production cost</span>
            <CurrencyInput
              value={values.productionCost}
              onChange={(ev) => onPatch({ productionCost: ev.target.value })}
              placeholder="Production spend"
              required
            />
          </label>
          <label className="eventos-field">
            <span className="eventos-field-label">Marketing cost</span>
            <CurrencyInput
              value={values.marketingCost}
              onChange={(ev) => onPatch({ marketingCost: ev.target.value })}
              placeholder="Marketing spend"
              required
            />
          </label>
          <label className="eventos-field">
            <span className="eventos-field-label">Artist fee</span>
            <CurrencyInput
              value={values.artistFee}
              onChange={(ev) => onPatch({ artistFee: ev.target.value })}
              placeholder="Artist fee"
              required
            />
          </label>
        </div>
      </div>
    </>
  );
};

export default EventFormFields;
